import React, { useEffect, useState, useRef, useMemo } from "react";
import { connect } from "react-redux";
import API from "../../../utils/API";
import "../Messenger.css";
import { useNavigate } from "react-router-dom";
import MesTable from "../../../components/Table/MesTable";
import editIcon from "../../../assets/icons/table/edit.svg";
import trashIcon from "../../../assets/icons/table/trash.svg";
import constants from "../../../utils/constants";
import commonMethods from "../../../utils/common-methods";
import { FormattedMessage } from "react-intl";
import useInfiniteScroll from "../../../components/common/useInfiniteScroll";
import NotificationModal from "../../../components/MessageManager/NotificationModal";
import dustbin from "../../../assets/icons/message-manager/dustbin.svg";
import { trackEvent } from "../../../utils/analytics";

function LatestScheduledMessagesTable(props) {
  const { updateFlag, setUpdateFlag, onLoadScheduledTable } = props;
  const navigate = useNavigate();
  const { user } = props;
  const permissionIds = user.permissions.map((permission) => permission.id);
  const [data, setData] = useState([]);
  const [dataCount, setDataCount] = useState(0);
  const [totalDataCount, setTotalDataCount] = useState(0);
  const [searchValue, setSearchValue] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState({});
  const [sortByCol, setSortByCol] = useState("nextRun");
  const [sortByAsc, setSortByAsc] = useState(false);
  const [startIndexForTable, setStartIndexForTable] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [selectedMessage, setSelectedMessage] = useState("");
  const [messagesData, setMessagesData] = useState([]);

  const tableRef = useRef();
  const tableWrapperRef = useRef();
  const modalRef = useRef(null);
  const hasTrackedRef = useRef(false);

  useEffect(() => {
    modalRef.current = new window.bootstrap.Modal(
      document.getElementById("notificationModal"),
    );
  }, []);

  const loadScheduledTable = async (map, resetSelection = true) => {
    map.data = {};
    map.ids = [];
    map.sortedIds = [];
    map.filteredIds = [];
    if (resetSelection) {
      map.selectedIds = {};
    }

    try {
      const res = await API.getScheduledMessages();
      res.data.forEach((mess) => {
        map.data[mess.id] = {
          id: mess.id,
          message: mess.title,
          type: mess.format,
          nextRun: commonMethods.formatDate(mess.scheduler.timeNextRun),
          groups: mess.groups.map((role) => role.name).join(", "),
          checked: false,
        };
      });
      map.filteredIds = Object.keys(map.data);
      map.ids = Object.keys(map.data);
      setMessagesData(res.data);
      setDataCount(map.ids.length);
      setTotalDataCount(map.ids.length);
      setDataLoaded(true);

      // Track dashboard view once on initial load
      if (!hasTrackedRef.current && res.data.length > 0) {
        hasTrackedRef.current = true;
        const now = new Date();
        const messageTypes = {};
        let totalHoursUntilNextRun = 0;
        let countWithNextRun = 0;

        res.data.forEach((mess) => {
          // Count message types
          const type = mess.format || "unknown";
          messageTypes[type] = (messageTypes[type] || 0) + 1;

          // Calculate hours until next run
          if (mess.scheduler?.timeNextRun) {
            const nextRun = new Date(mess.scheduler.timeNextRun);
            const hoursUntil = (nextRun - now) / (1000 * 60 * 60);
            if (hoursUntil > 0) {
              totalHoursUntilNextRun += hoursUntil;
              countWithNextRun++;
            }
          }
        });

        trackEvent(
          "message_manager.dashboard_scheduled",
          {
            scheduled_message_count: res.data.length,
            message_type_ticker_count: messageTypes["ticker"] || 0,
            message_type_full_screen_count: messageTypes["full-screen"] || 0,
            message_type_half_screen_count: messageTypes["half-screen"] || 0,
            has_ticker_messages: (messageTypes["ticker"] || 0) > 0,
            has_full_screen_messages: (messageTypes["full-screen"] || 0) > 0,
            has_half_screen_messages: (messageTypes["half-screen"] || 0) > 0,
            avg_hours_until_next_run:
              countWithNextRun > 0
                ? Math.round(totalHoursUntilNextRun / countWithNextRun)
                : 0,
          },
          user,
        );
      }
    } catch (err) {
      // If it's error 502, skip error handling
      if (err.response && err.response.status === 502) return;
      setError(err.message);
      setDataLoaded(true);
    }
  };

  useEffect(() => {
    if (onLoadScheduledTable) {
      onLoadScheduledTable(() => loadScheduledTable(dataMap));
    }
  }, [onLoadScheduledTable]);

  const handleEditClick = (e, id) => {
    e.preventDefault();
    navigate(`/message-manager/create-message`, { state: { id } });
  };

  const dataMap = useMemo(() => {
    const map = {
      data: {},
      ids: [],
      selectedIds: {},
      filteredIds: [],
      sortedIds: [],
    };
    loadScheduledTable(map);
    return map;
  }, []);

  useEffect(() => {
    loadScheduledTable(dataMap);
  }, [updateFlag]);

  // Hook for infinite scrolling
  const { generateDataSlice, sortMap, setSortBy } = useInfiniteScroll({
    dataMap,
    setData,
    setStartIndexForTable,
    startIndexForTable,
    pageSize: 100,
    sortByCol,
    setSortByCol,
    sortByAsc,
    setSortByAsc,
    tableWrapperRef,
    searchValue,
    tableRef,
    rowHeight: 48,
  });

  // Hook for sorting data when data changes or sort column is changed
  useEffect(() => {
    if (dataLoaded) {
      if (searchValue !== "") {
        tableRef.current.search(dataMap);
      } else {
        sortMap(dataMap);
        setData(generateDataSlice(dataMap));
      }
    }
  }, [sortByCol, sortByAsc, dataLoaded, dataMap, messagesData]);

  const handleSingleDelete = async (e) => {
    e.preventDefault();

    try {
      await API.deleteMessages(selectedMessage);
      setSuccess("Messages deleted successfully");
      modalRef.current.hide();
      setUpdateFlag((prev) => !prev);
    } catch (error) {
      setError("Error deleting messages");
      modalRef.current.hide();
    }
  };

  const tableColumnArray = [
    {
      Header: "MESSAGE",
      id: "message",
      accessor: "message",
      isVisible: true,
    },
    {
      Header: "TYPE",
      id: "type",
      accessor: "type",
      isVisible: true,
    },
    {
      Header: "NEXT_RUN",
      id: "nextRun",
      accessor: "nextRun",
      isVisible: true,
    },
    {
      Header: "GROUPS",
      id: "groups",
      isVisible: true,
      accessor: "groups",
    },
    {
      Header: "",
      id: "actions",
      isVisible: true,
      width: 100,
      Cell: ({ row }) => (
        <div className="actions">
          <button
            className="btn"
            id="editBtn"
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              if (handleEditClick) handleEditClick(e, row.original.id);
            }}
          >
            <img
              src={editIcon}
              className="actionIcon editAction"
              alt={<FormattedMessage id="EDIT" />}
            />
          </button>
          <button
            className="btn"
            id="deleteBtn"
            data-bs-toggle="modal"
            data-bs-target="#notificationModal"
            onClick={(e) => {
              e.stopPropagation(); // Prevent click from propagating to the parent cell
              setSelectedMessage(row.original.id);
            }}
          >
            <img
              src={trashIcon}
              className="actionIcon deleteAction"
              alt={<FormattedMessage id="DELETE" />}
            />
          </button>
        </div>
      ),
    },
  ];

  const tableProps = {
    // The tableKey is for task#20542.
    // Each table requires a unique key to prevent preferences from being overwritten.
    tableKey: "sbmm_latest_scheduled_messages_table",
    pageType: constants.pageType.LatestScheduledMessages,
    modalId: "Status",
    columns: tableColumnArray,
    data,
    setData,
    dataMap,
    generateDataSlice,
    sortMap,
    dataCount,
    setDataCount,
    totalDataCount,
    setSortBy,
    sortByCol,
    sortByAsc,
    success,
    setSuccess,
    error,
    setError,
    startIndex: startIndexForTable,
    selectedIds,
    setSelectedIds,
    searchValue,
    setSearchValue,
    tableRef,
    tableWrapperRef,
    dataLoaded,
    initialContainerHeight: "288px",
  };

  const notificationProps = {
    icon: dustbin,
    message: "DELETE_MESSAGE",
    description: "DELETE_MESSAGE_DESCRIPTION",
    showSecondaryButton: true,
    primaryButtonText: "DELETE",
    onSubmit: (e) => handleSingleDelete(e),
  };

  return (
    <>
      <NotificationModal {...notificationProps} />
      <MesTable {...tableProps} />
    </>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

export default connect(mapStateToProps)(LatestScheduledMessagesTable);
